import { fetchDomains } from '@/api/domains';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import type { IUserDomain } from '@/entities/domain.entity';
import { $features } from '@/entities/features-entity';
import { PageScroll } from '@/layouts/page-layout';
import { useUnit } from 'effector-react';
import { GlobeIcon, InfoIcon, PlusIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import AddDomainModal from './add-domain-modal';
import DomainCard from './domain-card';

function formatDomainsSummary(domains: IUserDomain[]): string {
	if (domains.length === 0) return 'Подключение доменов для APK';

	const onlineCount = domains.filter(d => d.status === 'online').length;
	const domainWord = domains.length === 1 ? 'домен' : domains.length < 5 ? 'домена' : 'доменов';
	const onlineWord = onlineCount === 1 ? 'онлайн' : 'онлайн';

	return `${domains.length} ${domainWord} · ${onlineCount} ${onlineWord}`;
}

export default function DomainsPage() {
	const features = useUnit($features);
	const [domains, setDomains] = useState<IUserDomain[]>([]);
	const [loading, setLoading] = useState(true);
	const [addOpen, setAddOpen] = useState(false);
	const canAddDomain = features.domainsEnabled;

	const summary = useMemo(() => formatDomainsSummary(domains), [domains]);

	const loadDomains = useCallback(async (showSpinner = false) => {
		if (showSpinner) setLoading(true);
		try {
			const data = await fetchDomains();
			setDomains(data);
		} finally {
			if (showSpinner) setLoading(false);
		}
	}, []);

	useEffect(() => {
		void loadDomains(true);
	}, [loadDomains]);

	return (
		<PageScroll contentClassName='p-2 flex flex-col gap-3'>
			<Card className='gap-0 py-0 shadow-sm overflow-hidden'>
				<CardHeader className='border-b px-4 py-4 flex'>
					<div className='flex items-center justify-between gap-3 w-full'>
						<div className='flex items-center gap-3 min-w-0'>
							<div className='rounded-lg bg-chart-1/15 p-2 shrink-0'>
								<GlobeIcon className='text-chart-1' size={20} />
							</div>
							<div className='min-w-0'>
								<CardTitle className='text-lg text-chart-1'>Мои домены</CardTitle>
								<CardDescription>{summary}</CardDescription>
							</div>
						</div>
						<Button type='button' size='sm' onClick={() => setAddOpen(true)} disabled={!canAddDomain}>
							<PlusIcon size={16} className='mr-1' />
							Добавить
						</Button>
					</div>
				</CardHeader>

				<CardContent className='px-4 py-4'>
					{!canAddDomain ? (
						<div className='mb-4 flex items-start gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2.5 text-sm text-muted-foreground'>
							<InfoIcon className='mt-0.5 size-4 shrink-0' />
							<span>Добавление доменов временно отключено администратором.</span>
						</div>
					) : null}

					{loading ? (
						<div className='flex justify-center py-8'>
							<Spinner />
						</div>
					) : domains.length === 0 ? (
						<div className='flex flex-col items-center gap-3 py-8 text-center'>
							<div className='rounded-full bg-muted/60 p-3'>
								<GlobeIcon size={24} className='text-muted-foreground' />
							</div>
							<p className='text-sm text-muted-foreground max-w-[260px]'>
								Доменов пока нет. Добавьте домен и настройте NS у регистратора.
							</p>
						</div>
					) : (
						<div className='flex flex-col gap-3'>
							{domains.map(domain => (
								<DomainCard key={domain.id} domain={domain} />
							))}
						</div>
					)}
				</CardContent>
			</Card>

			<AddDomainModal open={addOpen} onOpenChange={setAddOpen} onCreated={() => void loadDomains()} />
		</PageScroll>
	);
}
